/**
 * Service rules of a brand (spec 019 D13, D17, D29, D33), edited on
 * `/admin/brand-settings` and saved through `updateBrandRules({ bookingRules })`.
 * Defaults and limits mirror the backend `brand-rules.types.ts` and
 * `brand-rules.validation.ts`.
 */

export interface ServiceCustomerCancellation {
  freeBeforeHours: number;
  lateFeePercent: number;
  startedFeePercent: number;
}

export interface ServiceRules {
  serviceCustomerCancellation: ServiceCustomerCancellation;
  serviceAutoConfirmHours: number;
  serviceRefundWindowHours: number;
  serviceLateCancelLimit: number;
  serviceNoShowLimit: number;
}

export type ServiceRuleKey = keyof ServiceRules;

export const SERVICE_RULE_KEYS: readonly ServiceRuleKey[] = [
  'serviceCustomerCancellation',
  'serviceAutoConfirmHours',
  'serviceRefundWindowHours',
  'serviceLateCancelLimit',
  'serviceNoShowLimit',
];

export const PLATFORM_SERVICE_RULES: ServiceRules = {
  serviceCustomerCancellation: { freeBeforeHours: 24, lateFeePercent: 50, startedFeePercent: 100 },
  serviceAutoConfirmHours: 24,
  serviceRefundWindowHours: 72,
  serviceLateCancelLimit: 3,
  serviceNoShowLimit: 2,
};

export const SERVICE_RULE_TITLES: Record<ServiceRuleKey, string> = {
  serviceCustomerCancellation: 'Job cancellation fees',
  serviceAutoConfirmHours: 'Job auto-confirm',
  serviceRefundWindowHours: 'Job refund requests',
  serviceLateCancelLimit: 'Late cancellations before a worker is paused',
  serviceNoShowLimit: 'No-shows before a worker is paused',
};

const num = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/** The brand's service rules, each field falling back to the platform value. */
export function serviceRulesOf(bookingRules: unknown): ServiceRules {
  const r = (bookingRules ?? {}) as Record<string, any>;
  const p = PLATFORM_SERVICE_RULES;
  const c = (r.serviceCustomerCancellation ?? {}) as Record<string, unknown>;
  return {
    serviceCustomerCancellation: {
      freeBeforeHours: num(c.freeBeforeHours, p.serviceCustomerCancellation.freeBeforeHours),
      lateFeePercent: num(c.lateFeePercent, p.serviceCustomerCancellation.lateFeePercent),
      startedFeePercent: num(c.startedFeePercent, p.serviceCustomerCancellation.startedFeePercent),
    },
    serviceAutoConfirmHours: num(r.serviceAutoConfirmHours, p.serviceAutoConfirmHours),
    serviceRefundWindowHours: num(r.serviceRefundWindowHours, p.serviceRefundWindowHours),
    serviceLateCancelLimit: num(r.serviceLateCancelLimit, p.serviceLateCancelLimit),
    serviceNoShowLimit: num(r.serviceNoShowLimit, p.serviceNoShowLimit),
  };
}

export interface ServiceRulesForm {
  freeBeforeHours: string;
  lateFeePercent: string;
  startedFeePercent: string;
  autoConfirmHours: string;
  refundWindowHours: string;
  lateCancelLimit: string;
  noShowLimit: string;
}

export type ServiceFormErrors = Partial<Record<keyof ServiceRulesForm, string>>;

export function toServiceForm(rules: ServiceRules): ServiceRulesForm {
  return {
    freeBeforeHours: String(rules.serviceCustomerCancellation.freeBeforeHours),
    lateFeePercent: String(rules.serviceCustomerCancellation.lateFeePercent),
    startedFeePercent: String(rules.serviceCustomerCancellation.startedFeePercent),
    autoConfirmHours: String(rules.serviceAutoConfirmHours),
    refundWindowHours: String(rules.serviceRefundWindowHours),
    lateCancelLimit: String(rules.serviceLateCancelLimit),
    noShowLimit: String(rules.serviceNoShowLimit),
  };
}

const LIMITS: Record<keyof ServiceRulesForm, { min: number; max: number; integer: boolean }> = {
  freeBeforeHours: { min: 0, max: 168, integer: false },
  lateFeePercent: { min: 0, max: 100, integer: false },
  startedFeePercent: { min: 0, max: 100, integer: false },
  autoConfirmHours: { min: 1, max: 168, integer: true },
  refundWindowHours: { min: 0, max: 720, integer: true },
  lateCancelLimit: { min: 1, max: 20, integer: true },
  noShowLimit: { min: 1, max: 20, integer: true },
};

/** Parses the form with the API's limits; every field is required. */
export function fromServiceForm(form: ServiceRulesForm): {
  rules: ServiceRules | null;
  errors: ServiceFormErrors | null;
} {
  const errors: ServiceFormErrors = {};
  const values = {} as Record<keyof ServiceRulesForm, number>;
  (Object.keys(LIMITS) as (keyof ServiceRulesForm)[]).forEach(key => {
    const { min, max, integer } = LIMITS[key];
    const text = form[key].trim();
    const value = Number(text);
    if (!text || !Number.isFinite(value)) errors[key] = 'Enter a number';
    else if (integer && !Number.isInteger(value)) errors[key] = 'Use a whole number';
    else if (value < min || value > max) errors[key] = `From ${min} to ${max}`;
    else values[key] = value;
  });
  if (Object.keys(errors).length) return { rules: null, errors };
  return {
    rules: {
      serviceCustomerCancellation: {
        freeBeforeHours: values.freeBeforeHours,
        lateFeePercent: values.lateFeePercent,
        startedFeePercent: values.startedFeePercent,
      },
      serviceAutoConfirmHours: values.autoConfirmHours,
      serviceRefundWindowHours: values.refundWindowHours,
      serviceLateCancelLimit: values.lateCancelLimit,
      serviceNoShowLimit: values.noShowLimit,
    },
    errors: null,
  };
}

/** Only the keys that differ from what was loaded (a whole object for the fees). */
export function changedServiceRules(
  loaded: ServiceRules,
  edited: ServiceRules,
): Partial<ServiceRules> {
  const out: Partial<ServiceRules> = {};
  SERVICE_RULE_KEYS.forEach(key => {
    if (JSON.stringify(loaded[key]) !== JSON.stringify(edited[key])) {
      (out as Record<string, unknown>)[key] = edited[key];
    }
  });
  return out;
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * The example under the fees: what cancelling a job costs, on the job's
 * services, add-ons and travel fee (the service fee and tax follow in the same
 * share; a handover counts as one hour), like the backend `job-cancellation.ts`.
 */
export function serviceCancellationExample(
  rules: ServiceRules,
  subtotal = 125.5,
): { free: string; late: string; started: string } {
  const c = rules.serviceCustomerCancellation;
  const cents = Math.round(subtotal * 100);
  const fee = (percent: number) => Math.round((cents * percent) / 100);
  const hours = c.freeBeforeHours;
  return {
    free:
      hours > 0
        ? `Cancel ${hours} hours or more before the window: free.`
        : 'Cancel before the window: free.',
    late:
      hours > 0
        ? `Cancel less than ${hours} hours before: ${money(fee(c.lateFeePercent))} of a ${money(cents)} job.`
        : '',
    started: `Cancel once the window has started: ${money(fee(c.startedFeePercent))} of a ${money(cents)} job.`,
  };
}

/** Words for one service rule value in the change history, or null for another key. */
export function describeServiceValue(key: string, value: unknown): string | null {
  if (!(SERVICE_RULE_KEYS as readonly string[]).includes(key)) return null;
  if (value === null || value === undefined) return 'platform default';
  switch (key as ServiceRuleKey) {
    case 'serviceCustomerCancellation': {
      const c = value as Partial<ServiceCustomerCancellation>;
      return `free until ${c.freeBeforeHours} h before, then ${c.lateFeePercent}%, ${c.startedFeePercent}% once started`;
    }
    case 'serviceAutoConfirmHours':
      return `after ${value} h`;
    case 'serviceRefundWindowHours':
      return `${value} h after confirming`;
    default:
      return String(value);
  }
}

/** Whether the brand sells cleaning and handover jobs. */
export function brandSellsServices(allowedProductTypes: readonly string[] | null | undefined) {
  return (allowedProductTypes ?? []).some(
    t => String(t).toUpperCase() === 'HOUSE_AND_VEHICLE_SERVICE',
  );
}
